/* eslint-disable no-param-reassign */
import React, { setGlobal, useState, useEffect } from "reactn";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
// Hooks
import addressHook from "../../../hooks/addressHooks";

// components
import AddressLookUpView from "./AddressLookupView";
import ManualAddress from "./ManualAddress";

// helper functions
import { autoCompleteAddress, findAddress, findAddressWithName } from "../../../services/Api";

const AddressLookUp = ({ addressId, showText, innerTitle, handleBlur, handleKeyup, values, errors, clearManualFields, handleChange, country, onMount, disableManualEntry }) => {
    const { setAddress, clearAddress, addressObj, createAddressSummary, getAddress } = addressHook();

    const [textarea, setTextarea] = useState(showText);

    /**
     * `manualEntry` boolean, If the user clicks to manually enter
     * if the item location is international and the location is labelling-recipient show manual entry
     */
    const [manualEntry, setManualEntry] = useState(country !== "Ireland");

    useEffect(() => {
        if (onMount) {
            onMount();
        }
    }, []);

    /**
     * `addressListMessage` A message to be displayed on above the results
     */
    const [addressListMessage, setaddressListMessage] = useState("");

    /**
     * `reformattedAddress` This is for display in the text box
     */
    const [reformattedAddress, setreformattedAddress] = useState([]);

    /**
     * `dataReturned` boolean, if data has been return from address API
     */
    const [dataReturned, setDataReturned] = useState(false);

    /**
     * `addresses` array of the addresses returned from the API
     */
    const [addresses, setAddresses] = useState([]);

    useEffect(() => {
        // If there is a prepopulated address set it
        const selectedAddress = getAddress(addressId, values);
        if (selectedAddress && selectedAddress.county && selectedAddress.country && selectedAddress.country.toUpperCase() === "IRELAND") {
            const addressSummary = createAddressSummary(selectedAddress);

            setreformattedAddress(addressSummary);

            // clear manually entered fields when editing and an address was added through address finder
            if (clearManualFields && selectedAddress.addressId) {
                clearManualFields();
            }

            // when editing the address is valid
            values[`${addressId}Valid`] = true;

            setTextarea(true);
        }
    }, [addressObj]);

    /**
     * `addressLookUp` queries the auto address API.
     *  https://www.autoaddress.ie/support/developer-centre/faqs/general
     *  @param {string} address - this is passed on to the API to return an address
     */
    const addressLookUp = addressVal => {
        // replace leading white space
        const add = addressVal.replace(/^\s+/g, "");

        // temporary address
        const tempAddresses = {
            displayName: add,
            className: "searchTerm"
        };
        // set the address to the values passed in
        values[addressId] = add;

        // Because a valid address has not been selected, set this flag to false
        values[`${addressId}Valid`] = false;

        setGlobal({
            values
        });

        if (!dataReturned) {
            setAddresses([tempAddresses]);
        }

        const addressCallback = response => {
            // add the options to the addresses array
            if (response.data.options.length > 0) {
                setAddresses(response.data.options);
                setDataReturned(true);
            } else {
                setDataReturned(false);
            }
        };

        // get request to the auto address API only after 6 characters. Exists in services/API file
        if (add.length > 5) {
            autoCompleteAddress(add, addressCallback);
        }
    };

    // used to close the address pop up.
    const cancelLookUp = () => {
        // reset message
        setaddressListMessage("");
        // set address array to empty array
        setAddresses([]);

        values[`${addressId}Valid`] = false;

        // when cancelling look up reset value
        values[addressId] = "";
    };

    // used to manually enter information.
    const manuallyEnter = () => {
        // set address array to empty array
        cancelLookUp();

        // show the manual entry form
        setManualEntry(true);

        // Flag to identify that it is a manual entry
        values[`${addressId}Manual`] = true;
        values.manualEntry = true;

        // set global values
        setGlobal({
            values
        });
    };

    const findAddressWithNameCallback = response => {
        if (response.data.options) {
            setaddressListMessage(`We have found a number of addresses like this. Please select an option below:`);
            setAddresses([]);
            setAddresses(response.data.options);
        }
    };

    // findaddress callback
    const findAddressCallback = response => {
        if (response.data.reformattedAddress) {
            // Set the particular address type to be valid
            values[`${addressId}Valid`] = true;

            setAddress(addressId, response.data);

            // clear manually entered fields whenever a new address is entered through the address finder
            if (clearManualFields) {
                clearManualFields();
            }

            setGlobal({
                values: { ...values, uniqueAddress: response.data.isUniqueAddress }
            });

            // reset message
            setaddressListMessage("");
            // set address array to empty array
            setAddresses([]);
        } else if (disableManualEntry) {
            setaddressListMessage("We couldn't find that address. Try searching again or enter the address manually.");
        } else {
            setaddressListMessage("We couldn't find that address. Try searching again.");
        }
    };

    const handleSelect = e => {
        // get the data attr that contains the address and the url
        const selectedAddress = e.target.getAttribute("data-address");
        const selectedAddressId = e.target.getAttribute("data-address-id");

        // set the address to the address the user has selected
        values[addressId] = selectedAddress;

        // set global values
        setGlobal({
            values
        });

        // Call find address API
        if (selectedAddressId) {
            findAddress(selectedAddressId, findAddressCallback);
        } else {
            let partOfAddresss = selectedAddress;

            // if there is a hyphen in the address, remove it
            if (selectedAddress.indexOf("-") > 0) {
                partOfAddresss = selectedAddress.substr(0, selectedAddress.indexOf("-"));
            }

            // if the selected address does not have an address id (e.g. if the lookup option is selected)
            findAddressWithName(partOfAddresss, findAddressWithNameCallback);
        }
    };

    const editAddress = () => {
        // hide textarea
        setTextarea(false);

        // reset values
        values.manualEntry = false;

        cancelLookUp();

        // when editing address, clear out previous address
        clearAddress(addressId);

        values[addressId] = "";

        // Because a valid address has not been selected, set this flag to false
        values[`${addressId}Valid`] = false;

        setGlobal({
            values
        });
    };

    return (
        <>
            <AddressLookUpView
                addressId={addressId}
                showText={false}
                innerTitle={innerTitle}
                handleBlur={handleBlur}
                handleKeyup={handleKeyup}
                values={values}
                errors={errors}
                textarea={textarea}
                reformattedAddress={reformattedAddress}
                editAddress={editAddress}
                manualEntry={manualEntry}
                addressLookUp={addressLookUp}
                addresses={addresses}
                disableManualEntry={disableManualEntry}
                handleSelect={handleSelect}
                cancelLookUp={cancelLookUp}
                addressListMessage={addressListMessage}
                manuallyEnter={manuallyEnter}
                manualForm={<ManualAddress addressId={addressId} handleChange={handleChange} handleKeyup={handleKeyup} values={values} errors={errors} handleBlur={handleBlur} country={country} />}
            />
        </>
    );
};

export default withRouter(AddressLookUp);

// props validation
AddressLookUp.propTypes = {
    addressId: PropTypes.string,
    showText: PropTypes.bool,
    innerTitle: PropTypes.string,
    handleBlur: PropTypes.func,
    handleKeyup: PropTypes.func,
    handleChange: PropTypes.func,
    values: PropTypes.object,
    errors: PropTypes.object,
    clearManualFields: PropTypes.func,
    onMount: PropTypes.func,
    country: PropTypes.string,
    disableManualEntry: PropTypes.bool
};
